/*  global_dpm_matching.ts
    – Pure matching engine between Global DPM allocation excel rows
      (trade_dates/{valueDate}/global_dpm_allocations) and the GLOBAL_DPM trades
      of the same date bucket. No firebase imports: shared by the webapp
      (upload / verification panel / date cards) and functions/src/index.ts
      (auto-allocation in tradeUpdated).

    Match key: isin | side | ORDER date — the excel's date column is the order
    date (its trade_dates bucket), matched against the trade's AllFunds Order
    Date (orderDateId). The trade doc itself is bucketed by SETTLEMENT date for
    the operations view; legacy trade docs without orderDateId fall back to
    their bucket date.
    Split basis: quantity first, then amount. When every row of an order carries
    a Quantity (fund units) the split is pro-rata by quantity and reconciled
    against the trade's filledQty; otherwise pro-rata by amount, reconciled
    against the trade's reference amount. Amounts never reconcile exactly (both
    sides carry the team's 0.4% buffer independently rounded), so the check is
    relative: |Σ rows − trade reference| / reference ≤ 1%. The buffer cancels
    out of a proportional split.
*/
import { GlobalDpmAllocationRow, Trade, TradeAllocation, isAllfundsTradeAllocatable, stripNoise } from './types';

export const GLOBAL_DPM_MATCH_TOLERANCE = 0.01; // 1%

/** Fields of GlobalDpmAllocationRow the matcher needs (Timestamps stripped so plain objects work). */
export type MatchableRow = Pick<GlobalDpmAllocationRow, 'id' | 'valueDate' | 'isin' | 'side' | 'portfolioId' | 'amount' | 'quantity'>;

export type GlobalDpmSplitBasis = 'quantity' | 'amount';

/** "06/09/2026" → 6 Sep 2026 (local). Strict dd/MM/yyyy: month 13 / day 32 return null instead of rolling over. */
export const parseDdMmYyyyText = (s: string): Date | null => {
    const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const [dd, mm, yyyy] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const d = new Date(yyyy, mm - 1, dd);
    return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd ? d : null;
};

export interface MatchableTrade {
    id: string;
    isin?: string;
    side: string;
    valueDate: string; // dd-MM-yyyy — the trade_dates bucket the trade lives in (caller supplies it; the write path targets this)
    orderDate?: string; // dd-MM-yyyy AllFunds Order Date — the MATCH date (falls back to valueDate when absent on legacy docs)
    grossAmountRequested?: number;
    netSettle: number;
    orderValue: number;
    filledQty: number;
    allocations?: TradeAllocation[];
    state?: string;
    source?: string;
    nav?: number;
    allfundsStatus?: string;
}

/** dateId = the trade_dates bucket id the trade doc sits under. */
export const tradeToMatchable = (t: Trade, dateId: string): MatchableTrade => ({
    id: t.id,
    isin: t.isin,
    side: t.side,
    valueDate: dateId,
    orderDate: t.orderDateId,
    grossAmountRequested: t.grossAmountRequested,
    netSettle: t.netSettle,
    orderValue: t.orderValue,
    filledQty: t.filledQty,
    allocations: t.allocations,
    state: t.state,
    source: t.source,
    nav: t.nav,
    allfundsStatus: t.allfundsStatus,
});

export const toDdMmYyyy = (d: Date): string =>
    `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;

/** Amount the excel rows reconcile against; legacy docs (pre-backfill) lack grossAmountRequested. */
export const globalDpmReferenceAmount = (t: Pick<MatchableTrade, 'grossAmountRequested' | 'netSettle' | 'orderValue'>): number =>
    t.grossAmountRequested || t.netSettle || t.orderValue || 0;

/** Relative error between the excel sum and the trade's reference amount (Infinity when no reference). */
export const globalDpmAmountsDiff = (excelSum: number, t: Pick<MatchableTrade, 'grossAmountRequested' | 'netSettle' | 'orderValue'>): number => {
    const ref = globalDpmReferenceAmount(t);
    return ref > 0 ? Math.abs(excelSum - ref) / ref : Infinity;
};

export interface GlobalDpmMatchGroup {
    key: string; // isin|side|valueDate
    isin: string;
    side: 'BUY' | 'SELL';
    valueDate: string;
    rows: MatchableRow[];
    amountSum: number;
    quantitySum: number;
    basis: GlobalDpmSplitBasis | null; // null: rows mix quantity-only and amount-only → unsplittable
}

export interface GlobalDpmProposal {
    group: GlobalDpmMatchGroup;
    trade: MatchableTrade;
    allocations: TradeAllocation[]; // proportional split, sums exactly to filledQty
    errorPct: number; // relative error on the group's basis: Σquantity vs filledQty, or Σamount vs the trade's reference amount
    needsReview: boolean; // error > 1% — never auto-applied, only from the verification panel
}

/** Why a group has no proposal — so nothing ever sits silently. */
export type GlobalDpmWaitingReason = 'no-trade' | 'trade-already-allocated' | 'trade-locked' | 'trade-not-settled' | 'mixed-basis';

export interface GlobalDpmWaitingGroup extends GlobalDpmMatchGroup {
    reason: GlobalDpmWaitingReason;
    /** trade the reason refers to, when one exists */
    tradeId?: string;
}

export interface GlobalDpmMatchResult {
    proposals: GlobalDpmProposal[]; // within tolerance — safe to auto-apply
    mismatches: GlobalDpmProposal[]; // single candidate but error > 1% — shown as ERROR, manual apply only
    ambiguous: GlobalDpmMatchGroup[]; // several qualifying same-key trades — rows can't be safely partitioned
    waiting: GlobalDpmWaitingGroup[]; // no candidate trade, with the reason why
}

const groupKey = (r: { isin: string; side: string; valueDate: string }) => `${r.isin}|${r.side}|${r.valueDate}`;

/** Quantity drives the split only when every row carries one; otherwise amounts, when every row carries one. (`x > 0` is NaN/undefined-safe.) */
export const splitBasis = (rows: MatchableRow[]): GlobalDpmSplitBasis | null =>
    rows.every((r) => (r.quantity ?? 0) > 0) ? 'quantity' : rows.every((r) => r.amount > 0) ? 'amount' : null;

/** Relative error on the group's own basis: Σquantity vs filledQty, or Σamount vs the reference amount. */
const groupDiff = (g: GlobalDpmMatchGroup, t: MatchableTrade): number =>
    g.basis === 'quantity'
        ? (t.filledQty > 0 ? Math.abs(g.quantitySum - t.filledQty) / t.filledQty : Infinity)
        : globalDpmAmountsDiff(g.amountSum, t);

/** Proportional split of filledQty by row quantities or amounts; rounding residual lands on the largest row so the sum is exact. */
export const proportionalSplit = (rows: MatchableRow[], filledQty: number, basis: GlobalDpmSplitBasis = 'amount'): TradeAllocation[] => {
    const weight = (r: MatchableRow) => (basis === 'quantity' ? r.quantity ?? 0 : r.amount);
    const total = rows.reduce((s, r) => s + weight(r), 0);
    // unreachable via matchGlobalDpmRows (splitBasis guards); loud instead of writing NaN allocations
    if (!(total > 0)) throw new Error(`proportionalSplit: no positive ${basis} weights`);
    const allocations = rows.map((r) => ({ portfolioId: r.portfolioId, qty: stripNoise(filledQty * (weight(r) / total)) }));
    const residual = stripNoise(filledQty - allocations.reduce((s, a) => s + a.qty, 0));
    if (residual !== 0) {
        const largest = allocations.reduce((max, a) => (a.qty > max.qty ? a : max), allocations[0]);
        largest.qty = stripNoise(largest.qty + residual);
    }
    return allocations;
};

/** A trade the matcher may allocate: settled AllFunds trade, not locked, nothing allocated yet. */
const isCandidate = (t: MatchableTrade): boolean =>
    isAllfundsTradeAllocatable(t) &&
    t.state !== 'LOCKED' &&
    !(t.allocations && t.allocations.length > 0);

const excludedReason = (t: MatchableTrade): GlobalDpmWaitingReason =>
    t.allocations && t.allocations.length > 0 ? 'trade-already-allocated'
        : t.state === 'LOCKED' ? 'trade-locked'
            : 'trade-not-settled';

export const matchGlobalDpmRows = (pendingRows: MatchableRow[], trades: MatchableTrade[]): GlobalDpmMatchResult => {
    const groups = new Map<string, GlobalDpmMatchGroup>();
    for (const row of pendingRows) {
        const key = groupKey(row);
        let g = groups.get(key);
        if (!g) {
            g = { key, isin: row.isin, side: row.side, valueDate: row.valueDate, rows: [], amountSum: 0, quantitySum: 0, basis: null };
            groups.set(key, g);
        }
        g.rows.push(row);
        g.amountSum = stripNoise(g.amountSum + row.amount);
        g.quantitySum = stripNoise(g.quantitySum + (row.quantity ?? 0));
    }
    for (const g of groups.values()) g.basis = splitBasis(g.rows);

    const candidatesByKey = new Map<string, MatchableTrade[]>();
    const excludedByKey = new Map<string, MatchableTrade[]>(); // same-key trades that failed candidacy → waiting reason
    for (const t of trades) {
        if (!t.isin) continue;
        // rows key on their bucket (= excel order date); trades key on their order date
        const key = groupKey({ isin: t.isin, side: t.side, valueDate: t.orderDate ?? t.valueDate });
        const target = isCandidate(t) ? candidatesByKey : excludedByKey;
        (target.get(key) ?? target.set(key, []).get(key)!).push(t);
    }

    const result: GlobalDpmMatchResult = { proposals: [], mismatches: [], ambiguous: [], waiting: [] };
    for (const group of groups.values()) {
        if (!group.basis) {
            // data error in the excel — surface it even before a trade exists
            result.waiting.push({ ...group, reason: 'mixed-basis' });
            continue;
        }
        const candidates = candidatesByKey.get(group.key) ?? [];
        if (candidates.length === 0) {
            const excluded = excludedByKey.get(group.key)?.[0];
            result.waiting.push({
                ...group,
                reason: excluded ? excludedReason(excluded) : 'no-trade',
                tradeId: excluded?.id,
            });
            continue;
        }
        const withinTolerance = candidates.filter((t) => groupDiff(group, t) <= GLOBAL_DPM_MATCH_TOLERANCE);
        if (withinTolerance.length === 1 || (withinTolerance.length === 0 && candidates.length === 1)) {
            const trade = withinTolerance[0] ?? candidates[0];
            const errorPct = groupDiff(group, trade);
            const proposal: GlobalDpmProposal = {
                group,
                trade,
                allocations: proportionalSplit(group.rows, trade.filledQty, group.basis),
                errorPct,
                needsReview: errorPct > GLOBAL_DPM_MATCH_TOLERANCE,
            };
            (proposal.needsReview ? result.mismatches : result.proposals).push(proposal);
        } else {
            // several same-key trades and none/many reconcile — rows can't be safely partitioned
            result.ambiguous.push(group);
        }
    }
    return result;
};
